import { User } from '@remarkable/ark-icons';
import { Divider, Typography } from '@remarkable/ark-web';
import { useNavigate } from '@tanstack/react-router';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { type FleetUser } from 'src/api/endpoints/cloudEnterprise.types';
import { useDeactivateUser } from 'src/api/queries/enterpriseAdmin';
import { Button, Input, Link, Modal } from 'src/components';

export function ConfirmDeactivationModal({
  member,
  closeModal,
  handleDeactivation,
}: {
  member: FleetUser;
  closeModal: () => void;
  handleDeactivation: () => void;
}) {
  const deactivateUser = useDeactivateUser();
  const [confirmInput, setConfirmInput] = useState('');
  const isConfirmed = confirmInput.toLowerCase() === 'deactivate';
  const navigate = useNavigate();

  const openUserDrawer = async () => {
    closeModal();
    await navigate({
      to: '.',
      search: { member: member.email },
      replace: false,
    });
  };

  const handleConfirmClick = () => {
    setConfirmInput('');
    closeModal();
    handleDeactivation();
  };

  return (
    <Modal.Content title="Deactivate user?">
      <div className="flex flex-col gap-y-24">
        <Typography
          variant="body-lg"
          as="div"
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <User size={24} className="mr-4 inline" />
          <b>{member.name}</b> <span>({member.email})</span>
        </Typography>
        <Divider />
        <Typography variant="body-md" as="div" className="text-left">
          Here’s what will happen:
          <ul className="mt-20 ml-16 flex list-outside list-disc flex-col items-start text-left">
            <li>The user will be unpaired from all paper tablets</li>
            <li>The user will no longer be able to log into this account</li>
            <li>All content and settings will remain on the paper tablet(s)</li>
            <li>
              The user will remain in your overview, and you can re-activate the
              user anytime
            </li>
            <li>
              Previously synced files will remain safe in the account cloud
              storage
            </li>
          </ul>
        </Typography>
        <Typography variant="body-md" as="div" className="text-left">
          If you want to delete all data and settings on the tablet(s) connected
          to this user, you need to do a{' '}
          <Link
            onClick={openUserDrawer}
            className="inline cursor-pointer underline decoration-current"
            as="button"
            trackingLocation={ComponentLocations.ENTERPRISE.MEMBERS_PAGE}
          >
            remote wipe
          </Link>{' '}
          first. Make sure to confirm that the remote wipe is completed, before
          you deactivate the user.{' '}
        </Typography>
        <Input
          label={
            <Typography variant="body-md" className="text-left">
              Please type <b>Deactivate</b> to confirm
            </Typography>
          }
          value={confirmInput}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setConfirmInput(e.currentTarget.value)
          }
          name="confirm-deactivation"
          data-cy="confirm-deactivation"
          className="w-full"
          required
          maxLength={150}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (isConfirmed) {
                handleConfirmClick();
              }
            }
          }}
        />
        <div className="flex justify-center gap-16">
          <Modal.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Modal.Close>

          <Button
            variant="primary"
            loading={deactivateUser.isPending}
            disabled={!isConfirmed}
            onClick={handleConfirmClick}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal.Content>
  );
}
